import React, { useEffect, useState } from "react";
import {
    Label,
    Input,
    TextArea,
    Checkbox,
    Toggle,
    Select,
    SelectOption,
    Combobox,
    ComboboxOption,
    ControlError,
    Tooltip
} from "@forge/common";
import { useDispatch, useSelector } from "react-redux";
import { getosTypesList, getosVersionsList } from "../store/create-course.action";
import { CREATE_COURSE_CONSTANTS } from "../utils/constants";
import { INewVMProps, IOSTypes } from "../model/index";
import { fetchFileList } from "../../iso-file-management/store/iso-file-management.action";
import { AppState } from "../../../../../common/store/combinedReducer";
import "../vm-template/vm-template.scss";
import "../../create-course/create-course.scss";

const NewVirtualMachine: React.FC<INewVMProps> = ({
    vmFormData,
    vmFormError,
    powerOnStatus,
    handleVMFormChange,
    cpuOptions,
    availableOsVersions,
    isEditMode,
}) => {
    const dispatch = useDispatch();
    // Determine if this is an existing VM from API
    const isExistingVMFromAPI = vmFormData.vmId !== "" && vmFormData.isFromAPI === true;
    // Determine if this is a newly created VM (has ID but not from API)
    const isNewlyCreatedVM = vmFormData.vmId !== "" && vmFormData.isFromAPI !== true;
    // For brand new VM creation (no ID yet)
    const isCreatingNewVM = vmFormData.vmId === "";
    const osListReducer = useSelector((state: AppState) => {
        return state.createCourseReducer.osList;
    });
    const isoFileList = useSelector((state: AppState) => {
        return state.isoFileManagementReducer.isoFileList;
    });
    const [OSList, setOSList] = useState<IOSTypes[]>([]);
    const [bootImageList, setBootImageList] = useState<any[]>([]);
    const [firmwareFocused, setFirmwareFocused] = useState<boolean>(false);

    useEffect(() => {
        dispatch(getosTypesList());
        dispatch(fetchFileList(1, true));
        /** Call the OS versions list based on the selected OS type in Edit Mode */
        if (vmFormData.osType) {
            dispatch(getosVersionsList(vmFormData.osType === 'Microsoft Windows' ? '1' : vmFormData.osType === 'Linux' ? '2' : '3'));
        }
    }, [])
    useEffect(() => {
        if (Array.isArray(osListReducer) && osListReducer.length > 0) {
            setOSList(osListReducer);
        } else if (Array.isArray(osListReducer) && osListReducer.length === 0) {
            setOSList([]);
        }
    }, [osListReducer]);
    useEffect(() => {
        if (Array.isArray(isoFileList) && isoFileList.length > 0) {
            setBootImageList(isoFileList);
        } else if (Array.isArray(isoFileList) && isoFileList.length === 0) {
            setBootImageList([]);
        }
    }, [isoFileList]);
    const getDisplayLabelForOsVersion = (selectedValue: string, osVersions: any[]) => {
        if (!selectedValue || !osVersions.length) return '';
        const matchedVersion = osVersions.find(version =>
            version.osVersionValue.toLowerCase() === selectedValue.toLowerCase()
        );
        if (matchedVersion) {
            const displayName = matchedVersion.osVersionName;
            return displayName;
        }
        return selectedValue;
    };
    const getDisplayBootImageName = (selectedMediaId: string, bootImages: any[]) => {
        if (!selectedMediaId || !bootImages.length) return '';
        const matchedImage = bootImages.find(image => image.mediaId === selectedMediaId);
        return matchedImage ? matchedImage.name : selectedMediaId;
    };
    return (
        <div className={`grid grid-cols-2 mt-4 ${isCreatingNewVM ? 'b-top-1' : ''}`}>
            {/* LEFT COLUMN */}
            <div className={`w-[413px] ${isCreatingNewVM ? 'mt-4' : ''}`}>
                <div>
                    <Label required className="createCourse-label mb-2">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.VM_NAME.LABEL}</Label>
                    <Input
                        id="new-vm-name"
                        className={`createCourse-name-input-field ${!vmFormError.vmName ? '!border-none' : ''}`}
                        data-testid="new-vm-name"
                        size="regular"
                        placeholder="VM Name"
                        disabled={powerOnStatus}
                        value={vmFormData.vmName}
                        onChange={(e) => handleVMFormChange('vmName', e.target.value)}
                        error={!!vmFormError.vmName}
                        autoComplete="off"
                    />
                    {vmFormError.vmName === '' && (
                        <div className="text-sm text-gray-400 mt-1 text-xs">
                            Max 15 characters
                        </div>
                    )}
                    {vmFormError.vmName && (
                        <ControlError className="text-xs" showIcon={false}>{vmFormError.vmName}</ControlError>
                    )}
                </div>
                <div className="mt-4">
                    <Label className="createCourse-label mb-2">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.DESCRIPTION.LABEL}</Label>
                    <TextArea
                        id="vm-description"
                        className={`createCourse-description-input-field no-resize ${!vmFormError.description ? '!border-none' : ''}`}
                        data-testid="vm-description"
                        placeholder="Description"
                        disabled={powerOnStatus}
                        rows={3}
                        size="regular"
                        value={vmFormData.description}
                        onChange={(e) => handleVMFormChange('description', e.target.value)}
                        error={vmFormError.description !== ''}
                        autoComplete="off"
                    />
                    {vmFormError.description === '' && (
                        <div className="text-sm text-gray-400 mt-1 text-xs">
                            Max 250 characters
                        </div>
                    )}
                    {vmFormError.description && (
                        <ControlError className="text-xs" showIcon={false}>{vmFormError.description}</ControlError>
                    )}
                </div>
                <div className="h-[2.75rem] flex flex-col justify-end mb-4">
                    <Checkbox
                        id="auto-pause"
                        className="new-vm-input"
                        data-testid="vm-auto-pause"
                        checked={vmFormData.vmAutoPause}
                        disabled={powerOnStatus}
                        onChange={(e) => handleVMFormChange('vmAutoPause', e.target.checked)}>
                        {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.ALLOW_AUTO_PAUSE}
                    </Checkbox>
                </div>
                <div className="mt-9">
                    <Label required className="createCourse-label mb-2">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.CPU.LABEL}</Label>
                    <div className={`cpu-select-wrapper ${!vmFormError.cpu ? 'osVersion-select-no-border' : ''}`}>
                        <Combobox
                            autoComplete="off"
                            debounce={300}
                            height={5}
                            className={`createCourse-vpp-template font-style ${!vmFormError.cpu ? '!border-none' : ''}`}
                            data-testid="cpu-select"
                            disabled={powerOnStatus}
                            id="cpu-select"
                            i18nNoResults="No results"
                            i18nRemove=""
                            minCharacters={1}
                            onChange={(values) => {
                                values.length > 0 ? handleVMFormChange('cpu', values[0].value.toString()) : handleVMFormChange('cpu', '');
                            }}
                            placeholder="Select"
                            size="regular"
                            error={!!vmFormError.cpu}
                            value={vmFormData.cpu ? [{
                                label: vmFormData.cpu,
                                value: vmFormData.cpu
                            }] : []}
                        >
                            {cpuOptions.map((option, index) => (
                                <ComboboxOption
                                    key={index}
                                    label={option.label}
                                    value={option.value}
                                />
                            ))}
                        </Combobox>
                    </div>
                    {vmFormError.cpu && (
                        <ControlError className="text-xs" showIcon={false}>{vmFormError.cpu}</ControlError>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-4 mt-6 mb-6">
                    <div>
                        <Label required className="createCourse-label">
                            {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.RAM.LABEL}
                        </Label>
                        <div className="flex gap-2 mt-[8px]">
                            <div>
                                <Input
                                    id="ram"
                                    className={`flex-1 vm-input-storage !mt-0 ${!vmFormError.ram ? '!border-none' : ''}`}
                                    data-testid="ram"
                                    size="regular"
                                    disabled={powerOnStatus}
                                    placeholder={CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.RAM.PLACEHOLDER}
                                    value={vmFormData.ram}
                                    onChange={(e) => {
                                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                        handleVMFormChange('ram', numericValue);
                                    }}
                                    error={!!vmFormError.ram}
                                    autoComplete="off"
                                />
                                {vmFormError.ram && (
                                    <ControlError className="text-xs" showIcon={false}>
                                        {vmFormError.ram}
                                    </ControlError>
                                )}
                            </div>
                            <div className="filterBy">
                                <Select
                                    size="regular"
                                    className="w-24 createCourse-vpp-template !border-none !outline-none"
                                    id="ram-unit-select"
                                    height={3}
                                    disabled={powerOnStatus}
                                    data-testid="ram-unit-select"
                                    placeholder="Unit"
                                    defaultValue={vmFormData.ramUnit}
                                    onChange={(e) => {
                                        handleVMFormChange('ramUnit', e.target.value as 'MB' | 'GB');
                                    }}
                                >
                                    <SelectOption label="MB" value="MB" />
                                    <SelectOption label="GB" value="GB" />
                                </Select>
                            </div>

                        </div>
                    </div>
                    <div>
                        <Label required className="createCourse-label">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.STORAGE.LABEL}</Label>
                        <Input
                            id="vm-storage"
                            className={`vm-input-storage ${!vmFormError.storage ? '!border-none' : ''}`}
                            data-testid="vm-storage"
                            size="regular"
                            disabled={powerOnStatus}
                            suffix={<span className="text-sm font-normal">GB</span>}
                            placeholder={CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.STORAGE.PLACEHOLDER}
                            value={vmFormData.storage}
                            onChange={(e) => handleVMFormChange('storage', e.target.value)}
                            onBlur={() => {
                                handleVMFormChange('storage', vmFormData.storage);
                            }}
                            error={!!vmFormError.storage}
                            autoComplete="off"
                        />
                        {vmFormError.storage && (
                            <ControlError className="text-xs" showIcon={false}>{vmFormError.storage}</ControlError>
                        )}
                    </div>
                </div>
            </div>
            {/* RIGHT COLUMN */}
            <div className="w-[413px] mt-4">
                <div>
                    <Label required className="createCourse-label mb-2">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.OS_TYPE.LABEL}</Label>
                    <div className={`filterBy ${isExistingVMFromAPI || powerOnStatus ? 'select-wrapper-disabled' : ''}`}>
                        <Select
                            height={4}
                            className={`createCourse-vpp-template os-type-select font-style !outline-none ${!vmFormError.osType ? '!border-none' : ''} ${vmFormData.osType ? 'has-value' : ''}`}
                            data-testid="os-type-select"
                            id="os-type-select"
                            disabled={isExistingVMFromAPI || powerOnStatus}
                            onChange={(e) => handleVMFormChange('osType', e.target.value)}
                            placeholder="Select"
                            size="regular"
                            error={!!vmFormError.osType}
                            defaultValue={vmFormData.osType}
                        >
                            {OSList.length > 0 && (
                                OSList.map((os: IOSTypes, index: number) => (
                                    <SelectOption
                                        key={os.osTypeID || index}
                                        label={os.osTypeName}
                                        value={os.osTypeID}
                                    />
                                )))}
                        </Select>
                    </div>
                    {vmFormError.osType && (
                        <ControlError className="text-xs" showIcon={false}>{vmFormError.osType}</ControlError>
                    )}
                </div>
                <div className="mt-4">
                    <Label required className="createCourse-label mb-2">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.OS_VERSION.LABEL}</Label>
                    <div className={`os-version-select-wrapper ${!vmFormError.osVersion ? 'osVersion-select-no-border' : ''}`}>
                        {(() => {
                            const displayLabel = getDisplayLabelForOsVersion(vmFormData.osVersion, availableOsVersions);
                            const shouldShowTooltip = vmFormData.osVersion && vmFormData.osVersion.trim() !== '' && displayLabel && displayLabel.length > 20;
                            const comboboxElement = (
                                <Combobox
                                    autoComplete="off"
                                    debounce={300}
                                    height={5}
                                    className={`createCourse-vpp-template font-style !outline-none ${!vmFormError.osVersion ? '!border-none' : ''}`}
                                    data-testid="os-version-select"
                                    id="os-version-select"
                                    minCharacters={3}
                                    onChange={(values) => {
                                        values.length > 0 ? handleVMFormChange('osVersion', String(values[0].value)) : handleVMFormChange('osVersion', '');
                                    }}
                                    placeholder="Select"
                                    size="regular"
                                    error={!!vmFormError.osVersion}
                                    disabled={!vmFormData.osType || isExistingVMFromAPI || powerOnStatus}
                                    value={vmFormData.osVersion && vmFormData.osVersion.trim() !== '' ? [{
                                        label: displayLabel,
                                        value: vmFormData.osVersion
                                    }] : []}
                                >
                                    {availableOsVersions.map((version: any, index: number) => (
                                        <ComboboxOption
                                            key={version.osVersionID || index}
                                            label={version.osVersionName}
                                            value={version.osVersionValue}
                                        />
                                    ))}
                                </Combobox>
                            );

                            return shouldShowTooltip ? (
                                <Tooltip
                                    content={displayLabel}
                                    position="top"
                                    className="text-[#303030] text-sm"
                                >
                                    {comboboxElement}
                                </Tooltip>
                            ) : comboboxElement;
                        })()}
                    </div>
                    {vmFormError.osVersion && (
                        <ControlError className="text-xs" showIcon={false}>{vmFormError.osVersion}</ControlError>
                    )}
                </div>
                <div className="h-[10rem] flex flex-col justify-end">
                    <div>
                        <Label className="powerOn-label m-0">
                            {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.BOOT_SETUP}
                        </Label>

                    </div>
                    <div className={`mt-2 ${isExistingVMFromAPI || powerOnStatus ? 'boot-setup-disabled' : ''}`}>
                        <Toggle
                            id="boot-setup"
                            data-testid="bootSetup"
                            checked={vmFormData.bootSetup}
                            // disabled={isExistingVMFromAPI || powerOnStatus}
                            tabIndex={-1}
                            onChange={(e) => handleVMFormChange('bootSetup', e.target.checked)}
                        />
                    </div>
                </div>
                {/* ✅ Boot Image - Show for NEW VMs and NEWLY CREATED VMs (not from API) */}
                <div className="mt-4">
                    <Label required className="createCourse-label mb-2">
                        {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.BOOT_IMAGE}
                    </Label>
                    <div className={`cpu-select-wrapper ${!vmFormError.bootImage ? 'osVersion-select-no-border' : ''}`}>
                        <Combobox
                            autoComplete="off"
                            debounce={300}
                            height={5}
                            className={`createCourse-vpp-template font-style ${!vmFormError.bootImage ? '!border-none' : ''}`}
                            data-testid="bootImage-select"
                            id="bootImage-select"
                            i18nNoResults="No results"
                            i18nRemove="Remove"
                            minCharacters={1}
                            disabled={isExistingVMFromAPI || powerOnStatus}
                            onChange={(values) => {
                                if (values.length > 0) {
                                    handleVMFormChange('bootImage', values[0].value);
                                    handleVMFormChange('bootImageName', values[0].label);
                                } else {
                                    handleVMFormChange('bootImage', '');
                                    handleVMFormChange('bootImageName', '');
                                }
                            }}
                            placeholder="Select"
                            size="regular"
                            error={!!vmFormError.bootImage}
                            value={vmFormData.bootImage && vmFormData.bootImage.trim() !== '' ? [{
                                label: getDisplayBootImageName(vmFormData.bootImage, bootImageList),
                                value: vmFormData.bootImage
                            }] : []}
                        >
                            {bootImageList.map((option, index) => (
                                <ComboboxOption
                                    key={index}
                                    label={option.name}
                                    value={option.mediaId}
                                />
                            ))}
                        </Combobox>
                    </div>
                    {vmFormError.bootImage && (
                        <ControlError className="text-xs" showIcon={false}>{vmFormError.bootImage}</ControlError>
                    )}
                </div>
                <div className="mt-4">
                    <Label required className="createCourse-label mb-2">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.BOOT_FIRMWARE}</Label>
                    <div className={`cpu-select-wrapper ${!vmFormError.bootFirmware ? 'firmware-select-no-border' : ''}`}>
                        <Combobox
                            autoComplete="off"
                            debounce={300}
                            height={4}
                            className={`createCourse-vpp-template boot-firmware-select font-style !outline-none ${!vmFormError.bootFirmware ? '!border-none' : ''}`}
                            data-testid="boot-firmware-select"
                            id="boot-firmware-select"
                            disabled={isExistingVMFromAPI || powerOnStatus}
                            onFocus={() => setFirmwareFocused(true)}
                            onBlur={() => setFirmwareFocused(false)}
                            onChange={(values) => {
                                setFirmwareFocused(false);
                                values.length > 0 ? handleVMFormChange('bootFirmware', String(values[0].value)) : handleVMFormChange('bootFirmware', '');
                            }}
                            placeholder="Select"
                            size="regular"
                            minCharacters={0}
                            error={!!vmFormError.bootFirmware}
                            value={firmwareFocused ? [] : (vmFormData.bootFirmware ? [{
                                label: vmFormData.bootFirmware.toUpperCase() === 'BIOS' ? 'BIOS' : 'EFI',
                                value: vmFormData.bootFirmware
                            }] : [])}
                        >
                            <ComboboxOption
                                label={"BIOS"}
                                value={"bios"}
                            />
                            <ComboboxOption
                                label={"EFI"}
                                value={"efi"}
                            />
                        </Combobox>
                    </div>
                    {vmFormError.bootFirmware && (
                        <ControlError className="text-xs" showIcon={false}>{vmFormError.bootFirmware}</ControlError>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewVirtualMachine;